import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import { logCoralogix } from '@sunflower-games/coralogix-rum';
import { flow } from '@sunflower-games/flow-js';
import axios from 'axios';
import dayjs from 'dayjs';
import { FirebaseError } from 'firebase/app';
import type { AuthProvider, ApplicationVerifier, UserCredential, AdditionalUserInfo, User } from 'firebase/auth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  OAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  PhoneAuthProvider,
  signInWithRedirect,
  getAdditionalUserInfo,
  getRedirectResult,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  fetchSignInMethodsForEmail,
  signInWithCustomToken,
  linkWithCredential,
} from 'firebase/auth';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import type { WizardChildProps } from '../components/Wizard/types';
import {
  ACCESS_DENIED_ERROR,
  INACTIVE_ENTITY_ERROR,
  INCORRECT_CREDENTIAL,
  USER_EXCEEDED_REGISTRATION_LIMITATION_ERROR,
  USER_INACTIVE_ERORR,
} from '../config/constants';
import type { Wallet } from '../dto/wallet.types';
import type { AuthAction } from '../features/auth/useHandleAuth';
import type { CleanGeoData, Territory } from '../features/geography/types';
import i18n from '../i18n/config';
import { getErrorMessage } from '../utils/errors';
import { getUserLocation } from '../utils/getUserLocation';
import { devError } from '../utils/log';
import { getParam } from '../utils/marketingHelper';

import { analytics } from './analytics';
import { getRequest, postRequest } from './api';
import { logToNewRelic } from './debug';
import { firebaseAuth, prewarmRecaptcha } from './firebase';
import { getMediaForceTransactionId, googleTagEvent } from './googleTagEvent';
import { intercom } from './intercom';
import { createLDContext, identifyUser, ldClient } from './launchDarkly';
import { lifecycleEventEmitterService } from './lifecycleEventEmitter/LifecycleEventEmitterService';
import { queryClient } from './queryClient';
import { ReferralService } from './referral.service';
import { LOG_OUT_LISTENERS } from './shared';
import type {
  ApiResponse,
  EnumKycStatus,
  GeoData,
  OAuthPlatform,
  SelfExclusionCustomMsg,
  SignInPlatform,
} from './types';

export type Currency = 'USD' | 'CAD' | 'AUD' | 'EUR';
export type UserTutorialType = 'ONBOARDING' | 'BINGO';
export interface CurrencySymbol {
  code: Currency;
  symbol: string;
  id: string;
}

export interface UserTutorial {
  id: string;
  userId: number;
  type: UserTutorialType;
  finishedAt: Date | null;
}

export interface Profile {
  userId: number;
  uniqueId: string;
  firstName: string;
  lastName: string;
  email: string;
  nickname: string;
  isEmailVerified: boolean;
  phone: any;
  dateOfBirth: any;
  gender: any;
  hasVipHost: boolean;
  locale: any;
  signInCount: number;
  username: string;
  isActive: boolean;
  lastLoginDate: string;
  selfExclusion: any;
  selfExclusionUpdatedAt: any;
  disabledAt: any;
  disabledByType: any;
  disabledById: any;
  disableReason: any;
  newPasswordKey: any;
  newPasswordRequested: any;
  emailToken: any;
  address: string | null;
  city: string | null;
  zipCode: string | null;
  affiliateId: any;
  profileImage: any;
  currencyCode: Currency;
  currency: CurrencySymbol;
  documentLabels: any;
  requestedDocuments: any;
  level: number;
  loyaltyPoints: number;
  deviceType: string;
  tags: any;
  affiliateStatus: boolean;
  trackingToken: any;
  isAffiliateUpdated: any;
  otp: any;
  kycApplicantId?: string | null;
  createdAt: string;
  updatedAt: string;
  userDocuments: any[];
  isBonusClaimed: boolean;
  phoneVerified?: boolean;
  kycStatus?: EnumKycStatus;
  minRedeemableCoins: number;
  minimumCashRedeemableAmount: number;
  isOnboardingCompleted: boolean;
  isScPurchaseModeAvailable: boolean;
  isPscAvailable: boolean;
  isWscAvailable: boolean;
  isUserGotWelcomeBonus: boolean;
  depositCount: number;
  firebaseUid: string;
  finishedLoyaltyTutorial: boolean;
  clientIP: string;
  intercomHash: string;
  stats: Record<string, unknown> | string;
  userWallet: Wallet;
  finishedCoinBackTutorial?: boolean;
  finishedCrownPrimeWelcomeTutorial?: boolean;
  expiration: string | null;
  cleanGeoData: CleanGeoData | null;
  isCountrySetByUser: boolean;
  displayName: string;
  language: string;
  territory: Territory;
  exchangeRateToUsd?: number;
  userTutorials: UserTutorial[];
  ldAttr: string | null;
  ldBootstrapFlags: Record<string, any> | null;
}

export interface AuthParams {
  wizardProps?: WizardChildProps;
  action: AuthAction;
  provider: SignInPlatform;
  credentials?: {
    email: string;
    password: string;
  };
}

export type ThirdPartyStatus = {
  status: 'notThirdParty' | 'firstLogin' | 'nonFirstLogin' | 'thirdPartyFirstLogin' | 'error';
  data?: unknown;
};

const { VITE_CUSTOM_TOKEN_API_URL } = import.meta.env;

export const AUTH_FLOW_KEY = 'in_auth_flow';

class Auth {
  private cache = new Map<
    string,
    {
      date: number;
      profile: Profile;
    }
  >();

  private _signUpFirebaseUserInfo: AdditionalUserInfo | null = null;

  public readonly authConflictListeners = new Set<(email: string) => void>();

  addAuthConflictListener(listener: (email: string) => void) {
    this.authConflictListeners.add(listener);
  }

  get isAuthConflictListenersEmpty() {
    return this.authConflictListeners.size === 0;
  }

  public isAuthError = false;
  public isUserInactive = false;
  public isUserSelfExcluded = false;
  public selfExclusionCustomMsg: SelfExclusionCustomMsg = undefined;
  public accessAllowed: boolean | null = null;
  public geoData: GeoData | null = null;
  public userCoolDown: string | null = null;

  public async initCmsAuth() {
    await firebaseAuth.authStateReady();
    await this.handleCustomTokenLogin();
  }

  public async initAuth() {
    LOG_OUT_LISTENERS.add(this.signOut.bind(this));

    await firebaseAuth.authStateReady();

    await this.handleCustomTokenLogin();

    const token = await firebaseAuth.currentUser?.getIdToken();

    lifecycleEventEmitterService.emitMessage('ON_APP_AUTH_INFO_LOADED', {
      isAuthenticated: !!token,
    });

    if (!token) {
      // We aren't logged in, so there's nothing to do here
      return null;
    }

    flow.setAccessToken(token);

    try {
      await this.apiLogin();
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      firebaseAuth.onAuthStateChanged(async (user) => {
        let profile: Profile | null = null;

        if (user) {
          // @ts-expect-error - reloadUserInfo is a private property
          const { reloadUserInfo } = user;

          // this is a workaround for the issue that firebase not updating if an auth provider was removed.
          // sign out the user so he will login again and then we will have the correct provider data.
          if (reloadUserInfo?.providerUserInfo && reloadUserInfo.providerUserInfo.length < user.providerData.length) {
            await this.signOut();
            return;
          }

          profile = await this.getUserProfile();
          const idToken = await user.getIdToken();
          flow.setAccessToken(idToken);
        } else {
          this.isAgeConfirmed = false;
        }

        this.updateLaunchDarklyUserContext(profile);
      });
    } catch (err: any) {
      if (err.name === ACCESS_DENIED_ERROR) {
        throw err;
      }
      devError('Failed to init auth', err);
    }

    return this.getUserProfile();
  }

  public updateLaunchDarklyUserContext = (profile: Profile | null) => {
    if (ldClient) {
      const ldContext = createLDContext(profile);
      void identifyUser(ldContext);
    }
  };

  public async createCustomToken() {
    try {
      const response = await postRequest<ApiResponse<{ token: string }>>('/v2/user/create-custom-auth-token');
      return response.data.data.token;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error creating custom token: ${errorMessage}`);
      throw error;
    }
  }

  private async handleCustomTokenLogin() {
    const searchParams = new URLSearchParams(window.location.search);

    const urlParamToken = searchParams.get('token');
    if (urlParamToken) {
      const response = await axios.post(VITE_CUSTOM_TOKEN_API_URL, { accessToken: urlParamToken });
      const { customToken } = response.data;
      return await this.customTokenLogin(customToken);
    }

    const urlParamCustomToken = searchParams.get('customToken');
    if (urlParamCustomToken) {
      return await this.customTokenLogin(urlParamCustomToken);
    }
  }

  public async customTokenLogin(token: string) {
    try {
      const userCredential = await signInWithCustomToken(firebaseAuth, token);
      lifecycleEventEmitterService.emitMessage('ON_APP_CUSTOM_TOKEN_AUTHENTICATION_COMPLETED', {
        isAuthenticated: !!userCredential,
      });
      return userCredential;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error signing in with custom token: ${errorMessage}`);
      lifecycleEventEmitterService.emitMessage('ON_APP_CUSTOM_TOKEN_AUTHENTICATION_COMPLETED', {
        isAuthenticated: false,
      });

      if (error instanceof FirebaseError) {
        const errorObject = { message: error.message, code: error.code };
        logToNewRelic('Firebase: Error signing in with custom token', errorMessage, 'error', errorObject);
      }

      throw error;
    }
  }

  private tryCache(token: string) {
    if (this.cache.has(token)) {
      const cache = this.cache.get(token);
      if (cache && cache.date > Date.now() - 5000) {
        return cache.profile;
      }
    }
    return;
  }

  private setCache(token: string, profile: Profile) {
    this.cache.set(token, {
      date: Date.now(),
      profile,
    });
  }

  public async apiLogin(platform?: SignInPlatform) {
    try {
      this.isAuthError = false;
      const token = await firebaseAuth.currentUser?.getIdToken();
      if (!token) {
        void logToNewRelic('Firebase: id token is missing', 'id token is missing', 'warn');
        return null;
      }

      let profile = this.tryCache(token);

      if (!profile?.userId) {
        const urlParams = new URLSearchParams(window.location.search);

        const referrerUniqueId = ReferralService.getReferrerUniqueId();

        const signUpMarketingConsents = localStorage.getItem('signup-marketing-consents');

        let parsedSignUpMarketingConsents: Record<string, boolean> = {};

        try {
          parsedSignUpMarketingConsents = signUpMarketingConsents ? JSON.parse(signUpMarketingConsents) : {};
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          console.error(`Error parsing signUpMarketingConsents: ${errorMessage}`);
        }

        const data = {
          utmPromo: getParam(urlParams, 'utm_promo'),
          utmFeature: getParam(urlParams, 'utm_feature'),
          utmSource: getParam(urlParams, 'utm_source'),
          dynamoLinkId: urlParams.get('d10x_link_id'),
          referrerUniqueId,
          marketingConsents: parsedSignUpMarketingConsents,
          pageUrl: window.location.href,
          language: i18n.language,
          ...(document.referrer ? { referrer: document.referrer } : {}),
        };

        const geoData = await getUserLocation();

        const res = await postRequest<{ data: { user: Profile } }>('user/login', omitBy(data, isNil), {
          headers: {
            'x-geo-coords': geoData ? window.btoa(JSON.stringify(geoData)) : undefined,
          },
        });

        profile = res.data.user;
      }

      if (profile) {
        if (profile.signInCount === 0) {
          void analytics.sendEvent('sign_up', {
            step_name: 'sign_up',
            event_action: 'validation_2' as never,
            is_succeeded: 1,
            sign_up_platform: (platform ?? 'unknown') as never,
            user_id: firebaseAuth.currentUser?.uid ?? '',
            landingKey: this.getLandingQueryParamValue(),
          });
        }

        this.setCache(token, profile);
        intercom.identifyUser(profile);

        if (dayjs(profile.expiration).isAfter(dayjs()) && profile.selfExclusion) {
          this.userCoolDown = profile.expiration;
        }
      }

      return profile;
    } catch (err: any) {
      const error = err.response?.data?.errors[0];

      if (!error) {
        return null;
      }

      if (error.name === INCORRECT_CREDENTIAL || error.name === USER_EXCEEDED_REGISTRATION_LIMITATION_ERROR) {
        this.isAuthError = true;
        await this.signOut();
      }

      if (error.name === INACTIVE_ENTITY_ERROR) {
        this.isUserInactive = true;
      }

      if (error.name === USER_INACTIVE_ERORR) {
        this.isUserSelfExcluded = true;
        this.selfExclusionCustomMsg = error.fields?.playerMessage;

        const user = error.fields.user;
        if (user) {
          intercom.identifyUser(user);
        }
      }

      if (err.response && err.response.status === 403 && err.response.data?.errors) {
        const { errors } = err.response.data;
        if (Array.isArray(errors) && errors.length > 0) {
          const [payload] = err.response.data.errors;
          if (payload.name === ACCESS_DENIED_ERROR) {
            this.accessAllowed = false;
            this.geoData = payload.fields;
          }
        }
      }

      return null;
    }
  }

  public async getUserProfile(ignoreCache = false) {
    const token = await firebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }

    let profile;

    if (!ignoreCache) {
      profile = this.tryCache(token);
    }

    if (!profile) {
      const res = await getRequest<ApiResponse<Profile>>('/v2/user/profile');
      profile = get(res, 'data.data', null);
    }
    if (profile) {
      this.setCache(token, profile);
    }
    return profile;
  }

  public get userId() {
    if (firebaseAuth.currentUser && !firebaseAuth.currentUser.isAnonymous) {
      return firebaseAuth.currentUser.uid;
    }
  }

  // backward compatibility
  private checkIfFacebookProviderExists(user: User) {
    return user.providerData.some((provider) => provider.providerId === 'facebook.com');
  }

  public subscribeToAuthStateChange = async (
    callback: (profile: Profile | null, thirdPartyStatus: ThirdPartyStatus) => unknown,
  ) => {
    const thirdPartyAdditionalUserInfo = await this.handleRedirectResponse();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    return firebaseAuth.onAuthStateChanged(async (user) => {
      lifecycleEventEmitterService.emitMessage('ON_APP_AUTHENTICATION_STATUS_CHANGED', {
        isAuthenticated: !!user,
      });

      if (!user) {
        callback(null, { status: 'notThirdParty' });
        return;
      }

      void analytics.identify(user.uid);

      let providerIdFallback: string | null = user.providerData[0]?.providerId ?? null;
      try {
        const tokenResult = await user.getIdTokenResult();
        providerIdFallback = tokenResult.claims?.firebase?.sign_in_provider ?? providerIdFallback;
      } catch (err) {
        devError('Failed to get ID token result', err);
      }

      const platform = this.mapProviderIdToPlatform(
        (this._signUpFirebaseUserInfo ?? thirdPartyAdditionalUserInfo)?.providerId ?? providerIdFallback,
      );

      const isThirdPartyAuth = platform !== 'email';

      if (user.emailVerified || isThirdPartyAuth || this.checkIfFacebookProviderExists(user)) {
        const firstLoginUserInfo = this._signUpFirebaseUserInfo ?? thirdPartyAdditionalUserInfo;
        this._signUpFirebaseUserInfo = null; // reset only after reaching the verified branch

        const isInAuthFlow = sessionStorage.getItem(AUTH_FLOW_KEY);

        this.apiLogin(platform)
          .then((profile) => {
            if (isInAuthFlow) {
              this.sendAuthSuccessEvent(firstLoginUserInfo, platform, profile);
              sessionStorage.removeItem(AUTH_FLOW_KEY);
            }
            const thirdPartyStatus = this.checkThirdPartyAuth(firstLoginUserInfo, platform);
            callback(profile || null, thirdPartyStatus);
          })
          .catch((err) => {
            const errorMessage = getErrorMessage(err);
            if (isInAuthFlow) {
              this.sendAuthFailedEvent(firstLoginUserInfo, platform, errorMessage);
              sessionStorage.removeItem(AUTH_FLOW_KEY);
            }
            console.error(`Error during API login: ${errorMessage}`);
            callback(null, { status: 'notThirdParty' });
          });
        return;
      }

      callback(null, { status: 'notThirdParty' });
    });
  };

  private getLandingQueryParamValue() {
    return new URLSearchParams(window.location.search).get('landing') || null;
  }

  // handle login with a non-google platform after having an already existing google account
  public handleRedirectResponse = async () => {
    try {
      const res = await getRedirectResult(firebaseAuth);
      return res ? getAdditionalUserInfo(res) : null;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error after redirect: ${errorMessage}`);
      if (error instanceof FirebaseError && error.code === 'auth/account-exists-with-different-credential') {
        const startParams = this.getAuthParams();

        void analytics.sendEvent('sign_up', {
          step_name: 'sign_up',
          event_action: 'validation',
          is_succeeded: 0,
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          sign_up_platform: startParams?.provider!,
          user_id: '',
          landingKey: this.getLandingQueryParamValue(),
        });

        const { email } = error.customData ?? {};
        if (typeof email === 'string') {
          const signInMethods = await fetchSignInMethodsForEmail(firebaseAuth, email);
          if (signInMethods.includes('google.com')) {
            this.authConflictListeners.forEach((listener) => listener(email));
          }
        }
      }

      return null;
    }
  };

  public async signInWithEmail({ email, password }: { email: string; password: string }) {
    await this.signOut();

    try {
      await prewarmRecaptcha();
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email.trim(), password.trim());
      return userCredential.user;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error signing in with email: ${errorMessage}`);

      if (
        error instanceof FirebaseError &&
        !['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential'].includes(error.code)
      ) {
        const errorObject = { message: error.message, code: error.code, email };
        const errorMsg = getErrorMessage(error);
        logToNewRelic('Firebase: Error signing in with email', errorMsg, 'error', errorObject);
      }

      throw error;
    }
  }

  public async signUpWithEmail({ email, password }: { email: string; password: string }) {
    await this.signOut();

    try {
      await prewarmRecaptcha();
      const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email.trim(), password.trim());
      this._signUpFirebaseUserInfo = getAdditionalUserInfo(userCredential);
      return userCredential.user;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error signing up with email: ${errorMessage}`);

      if (
        error instanceof FirebaseError &&
        !['auth/email-already-exists', 'auth/email-already-in-use'].includes(error.code)
      ) {
        const errorObject = { message: error.message, code: error.code, email };
        const errorMsg = getErrorMessage(error);
        logToNewRelic('Firebase: Error signing up with email', errorMsg, 'error', errorObject);
      }

      throw error;
    }
  }

  private getAuthProvider(platform: OAuthPlatform): AuthProvider {
    switch (platform) {
      case 'apple':
        return new OAuthProvider('apple.com');
      case 'google':
        return new GoogleAuthProvider();
      case 'facebook': {
        const provider = new FacebookAuthProvider();
        provider.addScope('email');

        return provider;
      }
      default:
        throw new Error(`Unknown platform: ${platform as string}`);
    }
  }

  public sendRegisterSuccessEvent(platform: SignInPlatform, email: string | null, profile: Profile | null) {
    const params = this.getAuthParams();

    googleTagEvent('sign_up_verified', {
      email: String(email ?? firebaseAuth.currentUser?.email ?? params?.credentials?.email ?? 'N/A'),
      firebase_uid: firebaseAuth.currentUser?.uid ?? 'N/A',
      referrer_domain: window.location.hostname,
    });
    const signUpSuccessEventParams: AnalyticsEvents['sign_up'] = {
      step_name: 'sign_up',
      event_action: 'validation',
      is_succeeded: 1,
      sign_up_platform: platform,
      user_id: firebaseAuth.currentUser?.uid ?? '',
      landingKey: this.getLandingQueryParamValue(),
    };
    void analytics.sendEvent('sign_up', signUpSuccessEventParams);

    logCoralogix('sign_up', signUpSuccessEventParams);

    const createAccountSuccessEventParams: AnalyticsEvents['create_account_success'] = {
      event_category: 'sign_up',
      event_type: 'signup_platform',
      event_value: platform,
      event_action: 'impression',
    };
    void analytics.sendEvent('create_account_success', createAccountSuccessEventParams);
    logCoralogix('create_account_success', createAccountSuccessEventParams);

    const mediaForceTransactionId = getMediaForceTransactionId();
    if (mediaForceTransactionId) {
      analytics.sendMediaForceEvent('sign_up', {
        transactionId: mediaForceTransactionId,
      });
    }

    logToNewRelic('Firebase: User signed up', 'User signed up', 'info', {
      platform,
      email: email ?? firebaseAuth.currentUser?.email ?? params?.credentials?.email ?? 'N/A',
      firebaseUid: firebaseAuth.currentUser?.uid,
      emailVerified: firebaseAuth.currentUser?.emailVerified,
      countryCode: profile?.cleanGeoData?.countryCode,
      territoryKey: profile?.cleanGeoData?.territoryKey,
    });

    this.removeAuthParams();
  }

  public async authWithThirdPartyPlatform(platform: OAuthPlatform) {
    await this.signOut();

    try {
      const provider = this.getAuthProvider(platform);
      await signInWithRedirect(firebaseAuth, provider);
      return firebaseAuth.currentUser;
    } catch (error: any) {
      const debugInfo = {
        platform,
        userAgent: navigator.userAgent,
        currentUrl: window.location.href,
        referrer: document.referrer,
        cookieEnabled: navigator.cookieEnabled,
        cookies: document.cookie,
        firebaseUid: firebaseAuth.currentUser?.uid || null,
        isAnonymous: firebaseAuth.currentUser?.isAnonymous || false,
        providerData: firebaseAuth.currentUser?.providerData || [],
      };

      console.error('Error signing in with third party platform:', error, debugInfo);
      console.error(`Error signing in with third party platform: v2 ${JSON.stringify(error)}`, debugInfo);
      logToNewRelic('Error signing in with third party platform:', error, 'error', debugInfo);

      if (error instanceof FirebaseError) {
        const errorObject = {
          message: error.message,
          code: error.code,
          ...debugInfo,
        };
        const errorMessage = getErrorMessage(error);
        logToNewRelic('Firebase: Error signing in with third party platform', errorMessage, 'error', errorObject);
      }

      throw error;
    }
  }

  private mapProviderIdToPlatform(providerId: string | null): SignInPlatform {
    switch (providerId) {
      case 'apple.com':
        return 'apple';
      case 'google.com':
        return 'google';
      case 'facebook.com':
        return 'facebook';
      case 'password':
      case 'phone':
        return 'email';
      default:
        return 'google';
    }
  }

  private checkThirdPartyAuth(
    firstLoginUserInfo: AdditionalUserInfo | null,
    authPlatform: SignInPlatform,
  ): ThirdPartyStatus {
    if (!firstLoginUserInfo) {
      return { status: 'nonFirstLogin' };
    }

    const isNewUser = firstLoginUserInfo.isNewUser;
    const isThirdPartyAuth = authPlatform !== 'email';

    if (isNewUser) {
      return { status: isThirdPartyAuth ? 'thirdPartyFirstLogin' : 'firstLogin' };
    }
    return { status: 'nonFirstLogin' };
  }

  private sendAuthSuccessEvent(
    additionalUserInfo: AdditionalUserInfo | null,
    authPlatform: SignInPlatform,
    profile: Profile | null,
  ) {
    if (additionalUserInfo?.isNewUser) {
      this.sendRegisterSuccessEvent(authPlatform, firebaseAuth.currentUser?.email ?? null, profile);
    } else {
      void analytics.sendEvent('login_success', {
        event_category: 'sign_up',
        event_type: 'login_platform',
        event_value: authPlatform,
        event_action: 'impression',
      });
    }
  }

  private sendAuthFailedEvent(
    additionalUserInfo: AdditionalUserInfo | null,
    authPlatform: SignInPlatform,
    reason?: string,
  ) {
    if (!additionalUserInfo) {
      void analytics.sendEvent('login_failed', {
        event_category: 'sign_up',
        event_type: 'login_platform',
        event_value: authPlatform,
        event_action: 'impression',
        reason,
      });
      return;
    }
    const isNewUser = additionalUserInfo.isNewUser;

    if (!isNewUser) {
      void analytics.sendEvent('login_failed', {
        event_category: 'sign_up',
        event_type: 'login_platform',
        event_value: authPlatform,
        event_action: 'impression',
        reason,
      });
    } else {
      void analytics.sendEvent('create_account_failed', {
        event_category: 'sign_up',
        event_type: 'signup_platform',
        event_value: authPlatform,
        event_action: 'impression',
        reason,
      });
    }
  }

  public async sendEmailVerification() {
    const referrerUniqueId = ReferralService.getReferrerUniqueId();
    return postRequest('/v2/user/send-verify-mail', { referrerUniqueId, language: i18n.language });
  }

  public trackVerificationCompletion(callback?: () => unknown): VoidFunction {
    const interval = setInterval(() => {
      const user = firebaseAuth.currentUser;
      if (!user) {
        clearInterval(interval);
      } else {
        void user.reload().then(() => {
          if (user.emailVerified) {
            clearInterval(interval);
            if (typeof callback === 'function') {
              callback();
            }
          }
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }

  public async sendPasswordResetLink(email: string) {
    try {
      await sendPasswordResetEmail(firebaseAuth, email, {
        url: window.location.href,
      });

      logToNewRelic('Firebase: Password reset link sent', 'Password reset link sent', 'info', { email });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error sending password reset link: ${errorMessage}`);

      if (error instanceof FirebaseError) {
        const errorObject = { message: error.message, code: error.code };
        const errorMsg = getErrorMessage(error);
        logToNewRelic('Firebase: Error sending password reset link', errorMsg, 'error', errorObject);
      }

      throw error;
    }
  }

  public async signOut(isUserAction?: boolean) {
    if (this.isUserInactive) {
      this.isUserInactive = false;
    }

    let loggedOut: 0 | 1 = 0;

    try {
      await queryClient.cancelQueries();
      await firebaseAuth.signOut();
      queryClient.clear();
      intercom.restartSession(null);
      loggedOut = 1;
    } catch (error) {
      devError('Failed to sign out', error);
    } finally {
      if (isUserAction) {
        void analytics.sendEvent('log_out', {
          is_succeeded: loggedOut,
        });
      }
    }

    return loggedOut === 1;
  }

  public async deleteUser() {
    await firebaseAuth.currentUser?.delete();
  }

  public async checkIfPhoneVerified(phoneNumber: string) {
    let result = {
      isInUse: false,
      verified: false,
      exceededRegistrationLimit: false,
    };
    try {
      const response = await postRequest<
        ApiResponse<{
          isInUse: boolean;
          verified: boolean;
          exceededRegistrationLimit: boolean;
        }>
      >('/v2/user/check-phone-number', {
        phoneNumber,
      });
      result = get(response, 'data.data', result);
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      console.error(`Error checking phone verification: ${errorMessage}`);
    }
    return result;
  }

  public async sendPhoneVerificationCode(phone: string, applicationVerifier: ApplicationVerifier) {
    try {
      const provider = new PhoneAuthProvider(firebaseAuth);
      const response = await provider.verifyPhoneNumber(phone, applicationVerifier);

      logToNewRelic('Firebase: Phone verification code sent', 'Phone verification code sent', 'info', {
        phone,
        response,
      });

      return response;
    } catch (error: any) {
      console.error(`Error sending phone verification code: ${error?.message || JSON.stringify(error)}`);

      if (error instanceof FirebaseError) {
        const errorObject = { message: error.message, code: error.code };
        const errorMessage = getErrorMessage(error);
        logToNewRelic('Firebase: Error sending phone verification code', errorMessage, 'error', errorObject);
      }

      throw error;
    }
  }

  public getRecaptchaVerifier(visible?: boolean) {
    const parameters = visible ? undefined : { size: 'invisible' };
    const containerId = `${Date.now()}`;
    const container = document.createElement('div');
    container.id = containerId;
    container.style.setProperty('position', 'fixed');
    container.style.setProperty('bottom', '0.5rem');
    container.style.setProperty('right', '0.5rem');
    container.style.setProperty('z-index', '1000');
    container.style.setProperty('width', '186px');
    container.style.setProperty('height', '60px');
    document.body.appendChild(container);

    const verifier = new RecaptchaVerifier(firebaseAuth, container, parameters);

    const clear = verifier.clear.bind(verifier);

    verifier.clear = () => {
      clear();
      container.remove();
    };

    return verifier;
  }

  public async verifyPhone(verificationId: string, verificationCode: string, countryCode?: string) {
    const { currentUser } = firebaseAuth;

    if (!currentUser) {
      throw new Error("Can't verify phone for a logged out user.");
    }

    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);

    let userCredential: UserCredential | null = null;

    try {
      userCredential = await linkWithCredential(currentUser, credential);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error verifying phone: ${errorMessage}`);

      if (error instanceof FirebaseError) {
        const errorObject = { message: error.message, code: error.code };
        const errorMsg = getErrorMessage(error);
        logToNewRelic('Firebase: Error verifying phone', errorMsg, 'error', errorObject);
      }

      throw error;
    }

    const phoneVerificationMarketingConsents = localStorage.getItem('phone-verification-marketing-consents');

    let parsedPhoneVerificationMarketingConsents: Record<string, boolean> = {};

    try {
      parsedPhoneVerificationMarketingConsents = phoneVerificationMarketingConsents
        ? JSON.parse(phoneVerificationMarketingConsents)
        : {};
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error parsing phoneVerificationMarketingConsents: ${errorMessage}`);
    }

    const ownApiVerificationRes = await postRequest<{ data: { newGeo: CleanGeoData } }>('/v2/user/verify-phone', {
      phoneNumber: currentUser.phoneNumber,
      countryCode,
      marketingConsents: parsedPhoneVerificationMarketingConsents,
      pageUrl: window.location.href,
      ...(document.referrer ? { referrer: document.referrer } : {}),
    });

    return { userCredential, newGeo: ownApiVerificationRes.data.newGeo };
  }

  private static AGE_CONFIRMATION_KEY = 'ageConfirmed';

  public get isAgeConfirmed(): boolean {
    return sessionStorage.getItem(Auth.AGE_CONFIRMATION_KEY) === 'true';
  }

  public set isAgeConfirmed(isAgeConfirmed) {
    if (isAgeConfirmed) {
      sessionStorage.setItem(Auth.AGE_CONFIRMATION_KEY, `${isAgeConfirmed}`);
    } else {
      sessionStorage.removeItem(Auth.AGE_CONFIRMATION_KEY);
    }
  }

  public saveAuthParams(params: AuthParams) {
    localStorage.setItem('authParams', JSON.stringify(params));
  }

  public getAuthParams(): AuthParams | null {
    const params = localStorage.getItem('authParams');
    if (params) {
      try {
        return JSON.parse(params) as AuthParams;
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        console.error(`Error parsing auth params: ${errorMessage}`);
        return null;
      }
    }
    return null;
  }

  public removeAuthParams() {
    localStorage.removeItem('authParams');
  }

  public clearStatuses() {
    this.isAuthError = false;
    this.isUserInactive = false;
    this.isUserSelfExcluded = false;
    this.selfExclusionCustomMsg = undefined;
    this.accessAllowed = null;
    this.geoData = null;
    this.userCoolDown = null;
  }
}

export const auth = new Auth();
